/**
 * The profile SPA's SHELL-level copy: the sentence its flow-dispatch client shows when the session is the problem, and
 * the notice its profile surface shows when the ACCOUNT is.
 *
 * The tabs word themselves in their own leaves (`const/profile-contacts-copy.ts`, the personal-info copy). What is left
 * over is exactly what a viewer sees ABOUT THEMSELVES rather than about a tab, which is why both members below live in
 * one leaf: they are the two ways this SPA says "the problem is your account, not the thing you clicked".
 */

import { DEFAULT_ACCOUNT_STATUS } from '@federa/const/user-profile-defaults';

/** The shell sentences as ONE `*_COPY` record; the shape is argued on the navigator sibling. */
export const PROFILE_COPY = {
	/**
	 * The re-auth sentence `makeMfeFlowJsonClient` surfaces for a 401/403.
	 *
	 * It names the ACTION rather than the failure, on the Connect precedent: the spine routes here only for statuses
	 * that mean "authenticate again", and copy that said "couldn't load" would put a Try-again over a request that can
	 * never succeed until the viewer signs in.
	 */
	authError: 'Your session has expired. Sign in again to continue.',

	/**
	 * The label the spine prefixes onto a non-auth HTTP failure: `<label> (<status>): <server sentence>`.
	 *
	 * ONE label for the whole surface rather than one per flow, because the per-flow words are already on screen --
	 * every list view renders its own "Failed to load ..." for a query error, and this text reaches the viewer through
	 * `ErrorNotice` beside a mutation, where what matters is the status and the server's own sentence.
	 */
	requestErrorLabel: 'Profile request failed',

	/**
	 * The STILL signed-out state the standalone shell's content pane shows once the session has resolved to nobody.
	 *
	 * A sentence and a button, not a spinner: nothing is loading, the viewer has to act, and a spinner that says "wait"
	 * over a state that needs a click is a false promise. The login modal still opens on its own; this is what stands
	 * behind it and after it is dismissed.
	 */
	signedOut: 'Log in to see your profile.',

	/** The login modal's subtitle -- the app's one sentence of purpose, on the `<APP>_COPY.purpose` shape every other
	 *  standalone app's modal takes. It used to say "Sign in to ..." on a surface whose every button says Log in. */
	purpose: 'Manage your profile.',

	/** The mobile bar's overflow trigger. */
	moreTab: 'More',
} as const;

/**
 * The `profiles.account_status` values that are NOT a restriction.
 *
 * DERIVED from the writer-side default rather than transcribed. The column carries `TEXT DEFAULT 'active'` and NO CHECK
 * constraint, so there is no closed vocabulary to enumerate and any attempt to list "the bad ones" would go stale the
 * first time operations invents a status. The predicate is therefore inverted: everything that is not this is a
 * restriction, and an unrecognised one still reaches the viewer through {@link PROFILE_ACCOUNT_STATUS_COPY.fallback}.
 */
export const UNRESTRICTED_ACCOUNT_STATUSES: readonly string[] = [DEFAULT_ACCOUNT_STATUS];

/**
 * What a restricted account is told.
 *
 * Every entry names WHAT IS TRUE and WHAT TO DO, because the alternative -- which is what shipped until 2026-08-20 --
 * was silence: `SELECT_PROFILE_SQL` fetched `account_status`, `ProfileData` picked it, and no surface rendered it. A
 * suspended provider therefore saw a normal-looking profile whose writes failed for reasons never stated on screen.
 *
 * `fallback` is deliberately vaguer than the named entries and that is correct rather than lazy: it fires on a status
 * this copy has never heard of, so a confident sentence about what it means would be a guess. The surface renders the
 * raw status beside it in a Badge, so support can be told the exact word without this copy interpolating it.
 */
export const PROFILE_ACCOUNT_STATUS_COPY = {
	suspended: {
		title: 'Your account is suspended',
		body: 'You can still read your profile, but bookings, payouts and edits are blocked while the suspension stands. Contact support to have it reviewed.',
	},
	pending: {
		title: 'Your account is awaiting review',
		body: 'Your details are with our team. Some actions stay unavailable until the review finishes -- nothing else is required from you right now.',
	},
	inactive: {
		title: 'Your account is inactive',
		body: 'An inactive account cannot take bookings or payouts. Contact support to reactivate it.',
	},
	fallback: {
		title: 'Your account is not active',
		body: 'Some actions are unavailable while your account is in this state. Contact support with the status shown here and they can explain what it means.',
	},
} as const;

/** The Personal Info tab's own chrome and outcomes. */
export const PROFILE_PERSONAL_INFO_COPY = {
	loadFailed: 'Failed to load profile.',
	empty: 'No profile data available.',
	/** Saving a name changes text the viewer is already looking at, so the field alone confirms nothing -- an unchanged
	 *  box after a failed save looks identical to a successful one. */
	savedToast: 'Profile updated.',
	/** Accessible name for the raw-status badge beside the account notice; the word itself comes from the database. */
	accountStatusBadgeLabel: 'Account status',
	// The name form draws no heading and the hero no "managed by support" line (operator 2026-09-26): the field labels
	// name the form, and contact lines shown with no field to edit them already say they are not edited here.
	firstNameLabel: 'First name',
	lastNameLabel: 'Last name',
	/** The display-name sentinel when the record carries no name, username or email at all. */
	unknownName: 'Unknown User',
} as const;
